import { useState } from "react";
import Tippy from "@tippyjs/react";
import { useNavigate } from "react-router-dom";
import { HiChatAlt2, HiUser } from "react-icons/hi";
import UserAvatar from "../ui/UserAvatar";
import { useChat } from "./ChatProvider";
import { formatLastSeen, userName } from "./chatUtils";

// Wraps a person's name/photo in chat: clicking it shows a small profile card with
// "View profile" and "Message" actions.
function ProfileCardTrigger({ userId, children, placement = "right-start" }) {
  const { users, me, startDirectChat } = useChat();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const user = users[userId];
  if (!user) return children;

  const isMe = userId === me?.id;
  const go = (fn) => () => {
    setOpen(false);
    fn();
  };

  const card = (
    <div className="w-72 rounded-xl bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden" data-tour="chat-profile-card">
      <div className="h-14 bg-gradient-to-r from-[#2470B7] to-[#1d5a93]" />
      <div className="px-4 pb-4 -mt-8">
        <UserAvatar name={user.name} src={user.avatarUrl || user.avatarThumbUrl} seed={user.id} size="lg" online={!!user.online} className="ring-4 ring-white dark:ring-gray-800 rounded-full" />
        <p className="mt-2 text-base font-semibold truncate">{user.name}{isMe ? " (you)" : ""}</p>
        {(user.jobTitle || user.department) && (
          <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
            {[user.jobTitle, user.department].filter(Boolean).join(" · ")}
          </p>
        )}
        <p className="text-xs text-gray-500 truncate mt-0.5">{user.email}</p>
        <p className="text-xs text-gray-500 mt-0.5">{formatLastSeen(user)}</p>
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={go(() => navigate(isMe ? "/profile" : `/profile/${user.id}`))}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <HiUser className="w-4 h-4" /> View profile
          </button>
          {!isMe && (
            <button
              type="button"
              onClick={go(() => startDirectChat(user.id))}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2470B7] hover:bg-[#1d5a93] text-white text-sm font-medium"
            >
              <HiChatAlt2 className="w-4 h-4" /> Message
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Tippy
      theme="irg-plain"
      arrow={false}
      visible={open}
      onClickOutside={() => setOpen(false)}
      interactive
      placement={placement}
      appendTo={() => document.body}
      zIndex={90}
      content={card}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        aria-label={`Show ${userName(user)}'s profile`}
      >
        {children}
      </button>
    </Tippy>
  );
}

export default ProfileCardTrigger;
